import {EntityType} from 'common/report/util/saved-entities/types';
import {SearchDisplayContext} from 'common/report/util/search/types';

export interface QueryReportHeaderAttrs {
  'project-id': number;
  'entity-type': string;
  'selected-bookmark-id': number | null;
  'selected-bookmark-user-id': number | null;
  'selected-bookmark-user-name': string | null;
  'selected-bookmark-user-email': string | null;
  'selected-bookmark-name': string;
  'selected-bookmark-description': string;
  'hide-load-control': boolean;
  'invalid-entity': boolean;
  'invalid-message': string;
  'entities-loading': boolean;
  'computed-at': string;
  'control-items': Obj[];
  'display-context': SearchDisplayContext;
  'empty-bookmark-name': string;
  'has-unsaved-changes': boolean;
  'num-custom-alerts': number;
  'num-dashboards': number;
  'overflow-control-items': Obj;
  'project-as-of-date': string;
  'sampling-factor': number;
  'track-name': string;
  'user-id': number;
  'workspace-id': number;
  'is-save-failed': boolean;
  'hide-save-buttons': boolean;

  // permissions
  'can-write': boolean;
  'can-save': boolean;
  'can-share': boolean;
  'is-superadmin': boolean;
  'allow-staff-override': boolean;

  // sharing-disabled (visibility controls in create-entity-modal)
  'visibility-controls-message': string;
}

export const queryReportHeaderAttrsSchema = {
  'project-id': `number`,
  'entity-type': {
    type: `string`,
    enum: Object.values(EntityType),
    default: EntityType.Report,
  },
  'selected-bookmark-id': `number`,
  'selected-bookmark-user-id': `number`,
  'selected-bookmark-user-name': `string`,
  'selected-bookmark-user-email': `string`,
  'selected-bookmark-name': `string`,
  'selected-bookmark-description': `string`,
  'hide-load-control': {
    type: `boolean`,
    description: `Set hide-load-control=true to prevent display of the "Load" button`,
    default: false,
  },
  'invalid-entity': {
    type: `boolean`,
    description: `If entity is invalid, save will be prevented - create new or edit`,
    default: false,
  },
  'invalid-message': {
    type: `string`,
    description: `Optional tooltip to display above disabled "Save" button when entity is invalid`,
  },
  'entities-loading': `boolean`,
  'computed-at': `string`,
  'control-items': {
    type: `json`,
    default: [],
    description: `Additional set of items to be rendered in the control bar, interface ControlItem[]`,
  },
  'display-context': {
    type: `string`,
    enum: Object.values(SearchDisplayContext),
    description: `display context for the load entity modal: insights, flows, dashboards, etc (SearchDisplayContext)`,
  },
  'empty-bookmark-name': `string`,
  'has-unsaved-changes': `boolean`,
  'num-custom-alerts': `number`,
  'num-dashboards': `number`,
  'overflow-control-items': {
    type: `json`,
    default: [],
    description: `Items to be rendered in the overflow section of the control bar, interface ControlItem[]`,
  },
  'project-as-of-date': {type: `string`, description: `as of date for projects with data in the past.`},
  'sampling-factor': `number`,
  'track-name': {
    type: `string`,
    description: `If specified, dispatches '[mp-query-report-header] <event>' bubbled events that can be listened for tracking with tab=<track-name> as a property`,
  },
  'user-id': `number`,
  'workspace-id': `number`,
  'hide-save-buttons': {
    type: `boolean`,
    description: `display save options`,
    default: false,
  },

  // permissions
  'can-write': {
    type: `boolean`,
    description: `Can the user create/change entities?`,
    default: false,
  },
  'can-save': {
    type: `boolean`,
    description: `Can the user change the currently selected entity?`,
    default: false,
  },
  'can-share': {
    type: `boolean`,
    description: `Can the user share the currently selected entity?`,
    default: false,
  },
  'is-superadmin': `boolean`,
  'allow-staff-override': `boolean`,

  // sharing-disabled (visibility controls in create-entity-modal)
  'visibility-controls-message': {
    type: `string`,
    description: `Message to display above visibility controls`,
  },
  'is-save-failed': {
    type: `boolean`,
    description: `Save report failed`,
    default: false,
  },
};
